'use client'
import { useMutation, useQuery } from '@apollo/client/react'
import { useRouter } from 'next/navigation'
import { useContext, useEffect, useState } from 'react'
import { isNil } from 'ramda'
import { useLDClient } from 'launchdarkly-react-client-sdk'

import { isRadiusUser } from '~/helpers/auth'

import {
  GetAuthenticatedUserQuery,
  GetAuthenticatedUserQueryVariables,
  LogoutAuthenticatedUserMutation,
  LogoutAuthenticatedUserMutationVariables,
} from '~/hooks/useAuthentication/__generated_types__/queries.gql'
import { GET_AUTHENTICATED_USER, LOGOUT } from '~/hooks/useAuthentication/queries'
import { AuthenticatedUser, User } from '~/hooks/useAuthentication/types'

import { setUser } from '~/clients/sentryClient'
import {
  identifyUser,
  MixpanelEvent,
  register,
  resetMixpanel,
  track,
} from '~/clients/mixpanelClient'
import { AuthenticatedUserContext } from '~/providers/AuthenticatedUserContext'
import { Urls } from '~/constants/urls'

export const useAuthentication = () => {
  const router = useRouter()

  const [authenticatedUser, setAuthenticatedUser] = useState<User>(null)

  const ldClient = useLDClient()

  const {
    loading,
    refetch,
    data: authData,
    error: authError,
  } = useQuery<GetAuthenticatedUserQuery, GetAuthenticatedUserQueryVariables>(
    GET_AUTHENTICATED_USER,
    {
      notifyOnNetworkStatusChange: true,
    }
  )

  useEffect(() => {
    if (authError) {
      router.push('/login')

      return
    }

    if (authData?.currentUser) {
      const email = authData.currentUser.email || ''
      const role = authData.currentUser.role

      setUser(`${authData.currentUser.uuid}`, email)
      identifyUser(`${authData.currentUser.uuid}`)

      const sharedProperties = {
        role,
        Customer: authData.currentUser.customer?.name,
        'Is Internal User': email.includes('@ryder.com') || email.includes('@baton.io'),
      }
      const mixpanelProperties = {
        ...sharedProperties,
        email,
        $email: email,
        'Has Logged In': true,
      }

      register(mixpanelProperties)

      const runEffect = async () => {
        await ldClient?.identify({
          kind: 'user',
          name: authData.currentUser?.fullname || undefined,
          key: email,
          email,
          anonymous: false,
          custom: sharedProperties,
        })
        setAuthenticatedUser(authData.currentUser)
      }

      runEffect()
    }
  }, [authData, authError, router, ldClient])

  const [logout] = useMutation<
    LogoutAuthenticatedUserMutation,
    LogoutAuthenticatedUserMutationVariables
  >(LOGOUT, {
    onCompleted: (data) => {
      track(MixpanelEvent.UserLogout, { userId: data.logout.id })
      resetMixpanel()
      if (typeof window !== 'undefined' && window.Appcues) {
        window.Appcues.reset()
      }
      ldClient?.identify({
        kind: 'user',
        anonymous: true,
      })
      if (isRadiusUser(data.logout)) {
        router.push(Urls.login())
      } else {
        router.push(Urls.rydershare())
      }
    },
    onError: () => {
      router.push(Urls.login())
    },
  })

  return [authenticatedUser, logout, { loading, refetch }] as const
}

export const useAuthenticatedUser = (): AuthenticatedUser => {
  const authentication = useContext(AuthenticatedUserContext)
  const router = useRouter()

  const [user, logout, options] = authentication

  if (isNil(user) || isNil(logout) || isNil(options)) {
    router.push('/login')
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return [user!, logout!, options!] as const
}
